import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { DataRoomSheet } from 'router/subrouters/DataRoom/models/DataRoom';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { getArrayOfEmails } from 'utils';
import InputField from 'components/InputField';
import { Button, Loader } from 'ncoded-component-library';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import TextareaEmailsField from 'components/TextareaEmailsField';
import { FormApi } from 'final-form';
import validations from 'validations';
import { getArrayOfColleagueEmails } from '../BulkAddDataRooms/components/utils';
import EmailField from 'components/EmailField';

import './UpdateDataRoomDrawer.styles.scss';

type UpdateDataRoomForm = DataRoomSheet & {
  colleagues: string[];
  colleaguesUnsubmited: string;
};

type UpdateDataRoomDrawerProps = {
  className?: string;
  dataRoom: DataRoomSheet;
  updateDataRoom: (dataRoom: DataRoomSheet) => void;
  onClose?: () => void;
};

const UpdateDataRoomDrawer: ForwardRefRenderFunction<
  SideDrawerRef,
  UpdateDataRoomDrawerProps
> = (props, ref) => {
  const { className, dataRoom, updateDataRoom, onClose } = props;

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>();
  const formRef = useRef<FormApi<UpdateDataRoomForm>>(null);

  const { t } = useTranslation();
  const commonValidations = useValidations();

  useImperativeHandle(ref, () => drawer, [drawer]);

  const classes = classNames('update-data-room-drawer', className);

  const handleOnClose = useCallback(() => {
    drawer?.setOpen(false);
    formRef.current?.restart();
    formRef.current?.reset();
    onClose?.();
  }, [drawer, onClose]);

  const initialValues = useMemo(
    () =>
      dataRoom
        ? {
            dataRoomName: dataRoom.dataRoomName,
            ownerFirstName: dataRoom.ownerFirstName,
            ownerLastName: dataRoom.ownerLastName,
            ownerEmail: dataRoom.ownerEmail,
            colleagues: getArrayOfColleagueEmails(dataRoom.colleagues),
            colleaguesUnsubmited: '',
          }
        : {},
    [dataRoom],
  );

  const onSubmit = useCallback(
    async (values: UpdateDataRoomForm) => {
      const { colleagues, colleaguesUnsubmited, ...rest } = values;

      const finalEmails = getArrayOfEmails(colleagues, colleaguesUnsubmited);

      updateDataRoom({
        ...dataRoom,
        ...rest,
        _id: dataRoom?._id,
        colleagues: finalEmails.join(', '),
      });
      handleOnClose();
    },
    [dataRoom, updateDataRoom, handleOnClose],
  );

  return (
    <SideDrawer className={classes} ref={drawerRef} closeAction={handleOnClose}>
      {!dataRoom ? (
        <Loader />
      ) : (
        <Form
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={({ form, handleSubmit }) => {
            formRef.current = form;

            return (
              <form onSubmit={handleSubmit}>
                <p>{t('editDataRoom')}</p>

                <Field
                  name="dataRoomName"
                  label={t('dataRoomName')}
                  component={InputField}
                  showInitialError
                  required
                  validate={validations.required()}
                />

                <div className="row">
                  <Field
                    name="ownerFirstName"
                    label={t('ownerFirstName')}
                    component={InputField}
                    showInitialError
                    required
                    validate={commonValidations.name}
                  />
                  <Field
                    name="ownerLastName"
                    label={t('ownerLastName')}
                    component={InputField}
                    showInitialError
                    required
                    validate={commonValidations.name}
                  />
                </div>

                <EmailField
                  name="ownerEmail"
                  label={t('ownerEmail')}
                  showInitialError
                  required
                />

                <Field
                  name="colleagues"
                  label={t('colleagues')}
                  component={TextareaEmailsField}
                />

                <div className="actions">
                  <Button variant="link" onClick={handleOnClose}>
                    {t('close')}
                  </Button>
                  <Button type="submit">{t('updateDataRoom')}</Button>
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(UpdateDataRoomDrawer);
